import { useLayoutEffect } from 'react';

/*
 * Remembers where a list was scrolled to when the user opened one of its rows, and puts them back
 * there when they return.
 *
 * WHY this is not react-router's own <ScrollRestoration>: that component ships with the data
 * routers (createBrowserRouter), and this app renders routes under a plain <BrowserRouter> with
 * useRoutes, so it is not available to us.
 *
 * WHY sessionStorage rather than location.state: the user can come back two ways - the in-app
 * "back" that the detail screens do (navigate(state.from)) and the browser's own Back button. Only
 * the first can carry state, and the second is the one people actually use. sessionStorage catches
 * both, and is per-tab, so two tabs on the same list do not fight over one offset.
 *
 * The offset is written ONLY when leaving for a row (saveScrollPosition, called from the row's
 * click handler) and is deleted the moment it is consumed. That is what keeps a fresh visit from
 * the sidebar landing anywhere other than the top: with nothing stored, nothing is restored.
 */

const keyFor = (key: string) => `scroll-restore:${key}`;

export const saveScrollPosition = (key: string): void => {
  try {
    sessionStorage.setItem(keyFor(key), String(window.scrollY));
  } catch {
    /* Storage can be unavailable (private mode, quota). Losing a scroll offset is not worth
       breaking the navigation the user actually asked for. */
  }
};

export const hasSavedScrollPosition = (key: string): boolean => {
  try {
    return sessionStorage.getItem(keyFor(key)) !== null;
  } catch {
    return false;
  }
};

const takeSavedScrollPosition = (key: string): number | null => {
  try {
    const raw = sessionStorage.getItem(keyFor(key));
    if (raw === null) return null;
    sessionStorage.removeItem(keyFor(key));
    const top = Number(raw);
    return Number.isFinite(top) && top > 0 ? top : null;
  } catch {
    return null;
  }
};

/*
 * `ready` must only become true once the list has finished loading and its rows are on the page.
 * Restoring while a list is still showing its skeletons would scroll against a short document, and
 * the browser would clamp the offset to whatever fitted - putting the user near the top with no way
 * to tell why.
 *
 * It should become true even when the list came back EMPTY. The offset is consumed either way, so a
 * search that returns nothing cannot strand a stale offset for a later visit; with no rows to scroll
 * against the browser simply clamps it to the top, which is where an empty list belongs anyway.
 */
const useScrollRestoration = (key: string, ready: boolean): void => {
  useLayoutEffect(() => {
    if (!ready) return;

    const top = takeSavedScrollPosition(key);
    if (top === null) return;

    /* Layout effect plus a frame: the rows exist in the DOM by now, but MUI's charts and the card
       grid settle their final heights on the next paint. Scrolling before that lands short. */
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo(0, top);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [key, ready]);
};

export default useScrollRestoration;
